import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../components/index.js";
import "./log-view.js";
import "./json-tree.js";
import "./graph-view.js";
import { humanError } from "../api/errors.js";
import { getArtifactBlob } from "../api/builds.js";

type Kind = "pdf" | "graph" | "json" | "text";

// Text files above this are not rendered: every line becomes an element.
const TEXT_LIMIT = 2 * 1024 * 1024;

function kindOf(path: string): Kind | null {
  const p = path.toLowerCase();
  if (p.endsWith(".pdf")) return "pdf";
  if (p.endsWith(".bngraph")) return "graph";
  if (p.endsWith(".json")) return "json";
  if (p.endsWith(".log") || p.endsWith(".txt")) return "text";
  return null;
}

/** canView reports whether artifact-viewer can show the file at path. */
export function canView(path: string): boolean {
  return kindOf(path) !== null;
}

/** deepActive is the focused element, looked up through the shadow roots. */
function deepActive(): HTMLElement | null {
  let el = document.activeElement;
  while (el?.shadowRoot?.activeElement) el = el.shadowRoot.activeElement;
  return el instanceof HTMLElement ? el : null;
}

/**
 * artifact-viewer shows one build artifact in a dialog: a PDF in the browser's
 * own viewer, a .bngraph as a diagram, JSON as a tree, and .log/.txt as numbered
 * lines. It is controlled like bn-modal: the parent sets `path` (empty closes
 * it) and clears it on `close`. `download` asks the parent to save the file.
 *
 *   <artifact-viewer .buildId=${id} .path=${path} .size=${size}
 *     @close=${…} @download=${…}></artifact-viewer>
 */
@customElement("artifact-viewer")
export class ArtifactViewer extends LitElement {
  @property() buildId = "";
  /** The artifact to show; empty closes the dialog. */
  @property() path = "";
  /** The artifact's size in bytes, from the listing. */
  @property({ type: Number }) size = 0;

  @state() private phase: "loading" | "ready" | "error" | "too-large" | "no-pdf" = "loading";
  @state() private error = "";
  @state() private text = "";
  @state() private pdfUrl = "";

  // Counts the loads, so an answer for a file the user has left is dropped.
  private loads = 0;
  private opener: HTMLElement | null = null;

  static override styles = css`
    .content {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      padding: var(--bino-space-lg);
      outline: none;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .content > * {
      flex: 1;
    }
    .content.pdf {
      padding: 0;
    }
    iframe {
      border: 0;
      width: 100%;
    }
    .note {
      flex: none;
      margin: 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  override willUpdate(changed: PropertyValues) {
    if (!changed.has("path") && !changed.has("buildId")) return;
    if (this.path && !changed.get("path")) this.opener = deepActive();
    void this.load();
  }

  override updated(changed: PropertyValues) {
    if (!changed.has("path")) return;
    if (this.path) {
      // The content can take focus only once the modal has rendered its slot.
      const modal = this.renderRoot.querySelector("bn-modal");
      void modal?.updateComplete.then(() =>
        this.renderRoot.querySelector<HTMLElement>(".content")?.focus(),
      );
    } else {
      this.opener?.focus();
      this.opener = null;
    }
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.loads++;
    this.revoke();
  }

  private revoke() {
    if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl);
    this.pdfUrl = "";
  }

  private async load() {
    const load = ++this.loads;
    this.revoke();
    this.text = "";
    this.error = "";
    const kind = kindOf(this.path);
    if (!kind) return;
    if (kind === "pdf" && navigator.pdfViewerEnabled === false) {
      this.phase = "no-pdf";
      return;
    }
    if (kind !== "pdf" && this.size > TEXT_LIMIT) {
      this.phase = "too-large";
      return;
    }
    this.phase = "loading";
    try {
      const blob = await getArtifactBlob(this.buildId, this.path);
      if (load !== this.loads) return;
      if (kind === "pdf") {
        // The CSP lets the SPA frame blob URLs for this one case. The type is
        // fixed here and never taken from the file, so a framed blob is only
        // ever handed to the PDF viewer.
        this.pdfUrl = URL.createObjectURL(blob.slice(0, blob.size, "application/pdf"));
      } else {
        const text = await blob.text();
        if (load !== this.loads) return;
        this.text = text;
      }
      this.phase = "ready";
    } catch (e) {
      if (load !== this.loads) return;
      this.error = humanError(e);
      this.phase = "error";
    }
  }

  private renderContent(kind: Kind, name: string) {
    switch (this.phase) {
      case "loading":
        return html`<bn-spinner block label="Loading file…"></bn-spinner>`;
      case "error":
        return html`<bn-empty-state
          icon="⚠"
          heading="Couldn’t load the file"
          message=${this.error}
        ></bn-empty-state>`;
      case "too-large":
        return html`<p class="note">
          This file is larger than 2 MB and is not shown here. Download it to read it.
        </p>`;
      case "no-pdf":
        return html`<p class="note">
          This browser does not show PDF files in a page. Download the file to open it.
        </p>`;
    }
    switch (kind) {
      case "pdf":
        return html`<iframe src=${this.pdfUrl} title=${name}></iframe>`;
      case "graph":
        return html`<graph-view .text=${this.text}></graph-view>`;
      case "json":
        return html`<json-tree .text=${this.text}></json-tree>`;
      case "text":
        return html`<log-view .text=${this.text}></log-view>`;
    }
  }

  override render() {
    const kind = kindOf(this.path);
    const name = this.path.split("/").pop() ?? "";
    return html`<bn-modal fill ?open=${kind !== null} .width=${1600} heading=${this.path}>
      ${kind
        ? html`<div class="content ${this.phase === "ready" ? kind : ""}" tabindex="-1">
              ${this.renderContent(kind, name)}
            </div>
            <bn-button slot="footer" @click=${() => this.dispatchEvent(new CustomEvent("download"))}
              >Download</bn-button
            >`
        : nothing}
    </bn-modal>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "artifact-viewer": ArtifactViewer;
  }
}
